/**
 * @fileoverview Input/output schemas for the scene-explorer-v1 agent.
 *
 * This agent analyzes prose and proposes 4 distinct visual composition
 * approaches for the scene, each varying in shot type, framing, and
 * emotional emphasis.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 * Same input surface as story-image-prompt-creator-v2.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.charactersCurrent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.worldElements.schema)
  .merge(AGENT_SCHEMA_OBJECTS.selectedText.schema)
  .merge(AGENT_SCHEMA_OBJECTS.chapterContent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema);

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const CompositionIdeaSchema = z.object({
  id: z.string().describe('A short unique identifier for this composition, e.g. "comp-1".'),
  title: z.string().describe('A short, evocative title for this composition, e.g. "The Unspoken Threat".'),
  shotType: z.string().describe('The cinematographic shot type, e.g. "Extreme close-up", "Wide establishing shot", "Over-the-shoulder".'),
  description: z.string().describe('2-3 sentences describing what the viewer would see in this frame.'),
  emotionalFocus: z.string().describe('The primary feeling or theme this composition emphasizes.'),
  keyElements: z.array(z.string()).describe('Characters and objects that are focal in this composition.'),
});

export type CompositionIdea = z.infer<typeof CompositionIdeaSchema>;

export const OutputSchema = z.object({
  compositions: z.array(CompositionIdeaSchema).length(4).describe('Exactly 4 distinct composition ideas for visualizing the scene.'),
  characterNames: z.array(z.string()).optional().describe('An array of exact names of characters identified in the prose.'),
  worldElementNames: z.array(z.string()).optional().describe('An array of exact names of world elements identified in the prose.'),
});

export type Output = z.infer<typeof OutputSchema>;
